/*
 * Extracted from WalletPage, which had grown to 920 lines around four
 * module-level components. They were files in everything but name: each has
 * its own state, its own Stripe calls and its own error surface, and none of
 * them referenced the others' internals.
 */
import { useState } from 'react';
import { countryOptions, subdivisionLabel, subdivisionsFor } from '@/features/wallet/utils/countries';
import { formatUsd } from '@/shared/utils/format';
import type { BillingAddress } from '@/shared/types';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { Button } from '@/shared/ui';


// Shown only when a top-up is refused for its address — never up front. Billing
// exposes no way to read the stored address back, so there is nothing to
// pre-populate and no reliable way to ask "do you already have one?"; letting
// the refusal drive the prompt keeps it correct in both directions.
export function BillingAddressForm({
  reason,
  amountUsd,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  reason: 'billing_address_required' | 'billing_address_invalid';
  amountUsd: number;
  busy: boolean;
  error: string | null;
  onSubmit: (address: BillingAddress) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [country, setCountry] = useState('US');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [line1, setLine1] = useState('');

  // The dropdown can only emit canonical codes, so the old length check is gone;
  // the uppercase normalization stays because it is one line and the payload should
  // not depend on where the value came from.
  const trimmedCountry = country.trim().toUpperCase();
  const canSubmit = !busy;

  // null -> the subdivision is free text for this country. Only US/CA have lists —
  // the two countries where Stripe Tax resolves jurisdiction by subdivision code,
  // i.e. where a typo silently changes the tax owed rather than erroring.
  const subdivisions = subdivisionsFor(trimmedCountry);
  const stateLabel = subdivisionLabel(trimmedCountry);

  const handleCountryChange = (nextCountry: string) => {
    setCountry(nextCountry);
    // A subdivision belongs to its country: a leftover "NY" after switching to GB
    // would be posted verbatim to Stripe Tax as a British region. Clearing on every
    // change (not just list->text transitions) keeps the rule simple enough to hold.
    setState('');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    void onSubmit({
      country: trimmedCountry,
      state: state.trim() || null,
      postalCode: postalCode.trim() || null,
      city: city.trim() || null,
      line1: line1.trim() || null,
    });
  };

  return (
    <form className="wallet-address-form" onSubmit={handleSubmit} aria-label="Billing address">
      <p className="wallet-address-form__intro">
        {reason === 'billing_address_invalid'
          ? "We couldn't match that billing address to a tax region. Please check it and try again."
          : `We need your billing address to calculate sales tax on this ${formatUsd(amountUsd)} top-up.`}
      </p>

      {/* Three columns, and the DOM order IS the visual order (country, address,
          then the three short fields) so tab order never jumps around the form.
          Country + Address share the first row; City/State/Postal fill the second,
          which is what stops this reading as one very tall stack of full-width
          boxes and pushing the amount picker off-screen. */}
      <div className="wallet-address-form__grid">
        {/* A dropdown of country NAMES that emits ISO codes. The free-text original
            was capped at 2 chars, so "United States" became "Un" and Stripe Tax then
            refused it as unplaceable — an error only the user could fix and the form
            invited. The old "Two-letter country code" hint died with the input. */}
        <label>
          Country
          <select
            name="country"
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
            required
            autoComplete="country"
          >
            {countryOptions().map((option) => (
              <option key={option.code} value={option.code}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <label className="wallet-address-form__field--wide">
          Address
          <input
            type="text"
            name="address-line1"
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            maxLength={200}
            autoComplete="address-line1"
          />
        </label>
        <label>
          City
          <input
            type="text"
            name="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            maxLength={100}
            autoComplete="address-level2"
          />
        </label>
        {/* Subdivision: a dropdown where a list exists (US/CA), free text elsewhere.
            The blank first option is load-bearing — state is optional in the billing
            contract and an untouched field must submit as null, exactly like the
            empty text input. Defaulting to a real state would silently post a wrong
            tax jurisdiction for anyone who ignored the field. */}
        <label>
          {stateLabel}
          {subdivisions ? (
            <select
              name="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              autoComplete="address-level1"
            >
              <option value="">Select…</option>
              {subdivisions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              name="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength={100}
              autoComplete="address-level1"
            />
          )}
        </label>
        <label>
          Postal code
          <input
            type="text"
            name="postal-code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            maxLength={20}
            autoComplete="postal-code"
          />
        </label>
      </div>

      {error && <ErrorBanner message={error} />}

      {/* `button` / `button secondary` are this app's actual button classes (see
          styles.css and every other control on this page). These two were written as
          `btn` / `btn--primary`, which is a convention the stylesheet has never
          defined -- so both rendered as bare browser buttons in the middle of an
          otherwise styled wallet. */}
      <div className="wallet-address-form__actions">
        <Button type="submit" variant="primary" loading={busy}>
          {busy ? 'Saving…' : 'Save and continue'}
        </Button>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
